<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-16
-->

<script lang="ts" setup>
import { computed } from 'vue';
import { useSeoMeta, definePageMeta } from '#imports';
import { useTermsLang } from '~/composables/useTermsLang';
import termsKh from '../../i18n/locales/terms_kh';
import termsEn from '../../i18n/locales/terms_en';

// Disable default layout (no header / footer)
definePageMeta({
  layout: false,
});

const { currentLang } = useTermsLang();

const terms = computed(() => {
  return currentLang.value === 'en' ? termsEn : termsKh;
});

useSeoMeta({
  title: () => terms.value.seo.title,
  description: () => terms.value.seo.description,
  robots: 'noindex, nofollow',
});
</script>

<template>
  <div class="app-terms-page-wrapper">
    <div class="app-terms-container selection:bg-brand-red selection:text-white">
      <!-- Main Title -->
      <h1 class="main-title">{{ terms.hero.title }}</h1>
      <p class="subtitle">{{ terms.hero.subtitle }}</p>

      <!-- Intro Text -->
      <p class="intro-text" v-html="terms.intro.text"></p>

      <!-- Sections -->
      <div class="sections-list">
        <div v-for="section in terms.sections" :key="section.number" class="term-section">
          <h2 class="section-title">
            <span class="num">{{ section.number }}.</span> {{ section.title }}
          </h2>
          <p v-for="(p, idx) in section.paragraphs || []" :key="idx" class="paragraph" v-html="p"></p>

          <!-- Subsections -->
          <div v-if="section.subsections && section.subsections.length" class="subsections-list">
            <div v-for="sub in section.subsections" :key="sub.num" class="subsection">
              <h3 class="subsection-title">
                <span class="num">{{ sub.num }}</span> {{ sub.title }}
              </h3>
              <p v-for="(subP, subIdx) in sub.paragraphs || []" :key="subIdx" class="paragraph" v-html="subP"></p>

              <!-- Nested Subsections -->
              <div v-if="sub.subsections && sub.subsections.length" class="nested-subsections-list">
                <div v-for="nested in sub.subsections" :key="nested.num" class="nested-subsection">
                  <h4 class="nested-title">
                    <span class="num">{{ nested.num }}</span> {{ nested.title }}
                  </h4>
                  <p
                    v-for="(nestedP, nestedIdx) in nested.paragraphs"
                    :key="nestedIdx"
                    class="paragraph"
                    v-html="nestedP"
                  ></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-terms-page-wrapper {
  background: #ffffff;
  min-height: 100vh;
  width: 100%;
}

.app-terms-container {
  font-family: 'Kantumruy Pro', 'Kantumruy', 'Ubuntu', sans-serif;
  font-size: 12px;
  line-height: 1.7;
  color: #1f2937; /* Neutral gray-800 text color */
  padding: 24px 16px;
  background: #ffffff;
  max-width: 860px;
  margin: 0 auto;
}

.main-title {
  font-size: 16px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 4px;
  color: inherit;
}

.subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 20px;
}

.intro-text {
  margin-bottom: 24px;
  text-align: justify;
}

.sections-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.term-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  margin: 8px 0 4px 0;
  color: inherit;
}

.subsection {
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
}

.subsection-title {
  font-size: 12px;
  font-weight: 700;
  margin: 6px 0 2px 0;
  color: inherit;
}

.nested-subsection {
  margin-top: 6px;
  padding-left: 10px;
  border-left: 1.5px dashed #d1d5db;
}

.nested-title {
  font-size: 12px;
  font-weight: 600;
  margin: 4px 0 2px 0;
  color: inherit;
}

.paragraph {
  margin: 0 0 8px 0;
  text-align: justify;
}

.paragraph:last-child {
  margin-bottom: 0;
}

.num {
  font-weight: 700;
  margin-right: 4px;
}
</style>
